
import {
  computed,
  defineComponent,
  onMounted,
  onBeforeUnmount,
  ref,
} from 'vue';
import ResponsiveSize from '@/classes/ResponsiveSize';

export default defineComponent({
  props: {
    mediaSizes: {
      type: [String, Object],
      required: false,
    },
    mediaAlt: {
      type: String,
      required: false,
      default: () => null,
    },
    height: {
      type: String,
      required: false,
      default: () => 'default',
    },
    heightFixed: {
      type: String,
      required: false,
      default: () => null,
    },
    positionX: {
      type: String,
      required: false,
      default: () => 'center',
    },
    positionY: {
      type: String,
      required: false,
      default: () => 'center',
    },
    heightMobile: {
      type: String,
      required: false,
      default: () => 'inherit',
    },
    heightMobileFixed: {
      type: String,
      required: false,
      default: () => null,
    },
    positionMobileX: {
      type: String,
      required: false,
      default: () => 'inherit',
    },
    positionMobileY: {
      type: String,
      required: false,
      default: () => 'inherit',
    },
  },
  setup(props) {
    const root = ref<InstanceType<typeof HTMLElement>>();

    const mediaSources = computed<Record<string, Record<string, unknown>>>(() => {
      let ms = props.mediaSizes as string | Record<string, Record<string, unknown>>;

      if (typeof ms === 'string') {
        try {
          ms = JSON.parse(atob(ms || btoa('{}')));
        } finally {
          if (typeof ms === 'string') {
            ms = {};
          }
        }
      }

      return ms || {};
    });

    const imageSrc = ref<null | string>(null);
    const imageHeight = ref<null | string>(null);
    const imageWidth = ref<null | string>(null);
    const currentImageSrc = ref<null | Record<string, unknown>>(null);

    const setImageSource = () => {
      imageSrc.value = null;

      const sizeKeys = ['medium', 'large', 'full'];

      sizeKeys.forEach((key) => {
        if (
          root.value
          && mediaSources.value[key]
          && mediaSources.value[key].width
          && parseInt(`${mediaSources.value[key].width}`, 10) > root.value.offsetWidth
        ) {
          currentImageSrc.value = mediaSources.value[key];
          imageSrc.value = mediaSources.value[key].url as string;
        }
      });

      if (imageSrc.value === null) {
        const sourceKeys = Object.keys(mediaSources.value);

        if (sourceKeys.length >= 1) {
          currentImageSrc.value = mediaSources.value.full || mediaSources.value[sourceKeys[0]];
          imageSrc.value = currentImageSrc.value.url as string;
        }
      }
    };

    const imageType = ref(props.height);
    const align = ref(props.positionX);
    const valign = ref(props.positionY);

    const setImageHeight = () => {
      if (ResponsiveSize.getCurrentBreakpoint() === 'lg' || props.heightMobile === 'inherit') {
        if (props.height === 'fixed') {
          imageType.value = 'fixed';
          imageHeight.value = ResponsiveSize.getCSSValue(props.heightFixed as string);
        } else {
          imageType.value = props.height;
          imageHeight.value = null;
        }
      } else if (props.heightMobile === 'fixed') {
        imageType.value = 'fixed';
        imageHeight.value = `${props.heightMobileFixed}px`;
      } else {
        imageType.value = props.height;
        imageHeight.value = null;
      }
      if (ResponsiveSize.getCurrentBreakpoint() === 'lg' || props.positionMobileX === 'inherit') {
        align.value = props.positionX;
      } else {
        align.value = props.positionMobileX;
      }
      if (ResponsiveSize.getCurrentBreakpoint() === 'lg' || props.positionMobileY === 'inherit') {
        valign.value = props.positionY;
      } else {
        valign.value = props.positionMobileY;
      }
    };

    const setEventHandler = () => {
      window.addEventListener('resize', setImageHeight);
      window.addEventListener('resize', setImageSource);
      window.addEventListener('load', setImageSource);
      window.addEventListener('load', setImageHeight);
      setImageSource();
      setImageHeight();
    };

    const handleIntersect = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver,
    ): void => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setEventHandler();
          if (root.value) {
            observer.unobserve(root.value);
          }
        }
      });
    };

    const createObserver = (): void => {
      const observer = new IntersectionObserver(handleIntersect, {
        root: null,
        threshold: 0,
      });

      if (root.value) {
        observer.observe(root.value);
      }
    };

    onMounted(() => {
      setImageHeight();
      if (window.IntersectionObserver) {
        createObserver();
      } else {
        setEventHandler();
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', setImageHeight);
      window.removeEventListener('resize', setImageSource);
      window.removeEventListener('load', setImageSource);
      window.removeEventListener('load', setImageHeight);
    });

    const classes = computed<string[]>(() => {
      const cls = [
        'io-image-spacer',
        `io-image-spacer--${imageType.value}`,
        `align-${align.value}`,
        `valign-${valign.value}`,
      ];

      return cls;
    });

    return {
      root,
      mediaSources,
      imageSrc,
      imageHeight,
      imageWidth,
      currentImageSrc,
      classes,
      imageType,
    };
  },
});
